import type Feature from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'
import type { Extent } from 'ol/extent'
import { attributeLabel, hiddenAttributes } from '../map/layerAttributes'
import {
  dataTableKey,
  hideDataTable,
  refreshDataTable,
  showDataTable,
  type DataTableColumn,
  type DataTableRow,
} from './dataTable'

/** Rows per page — the former hard cap, now just a page size (T3). */
const PAGE_SIZE = 50

/** Columns are inferred from the first few features (WFS has no column list). */
const COLUMN_SAMPLE = 5

/**
 * WFS adapter for the shared data table (T3). It holds no rows of its own: the
 * source re-reads the live vector collection on every render, so features that
 * stream in after the table opened simply appear on the next refresh (see
 * `VectorModeController.onFeatures`).
 */
export interface AttributesRequest {
  /** Layer name — the table's owner key, echoed by onDataTableClose. */
  key: string
  title: string
  /** Live accessor, not a snapshot. */
  features: () => Feature<Geometry>[]
  /** Zoom the map to a row's geometry, when the caller can. */
  onZoomTo?: (extent: Extent) => void
}

export function showAttributesDrawer(request: AttributesRequest): void {
  // The table opens before the first GetFeature lands, so the columns cannot be
  // derived yet. Derive them on the first non-empty render, then freeze: a
  // header that kept re-deriving would shift under the user as later bboxes
  // bring features carrying extra keys.
  let columns: DataTableColumn[] = []
  const hidden = hiddenAttributes(request.key, request.title)
  showDataTable({
    key: request.key,
    title: request.title,
    pageSize: PAGE_SIZE,
    columns: () => {
      if (columns.length === 0) columns = columnsOf(request, hidden)
      return columns
    },
    // The whole feature collection is client-side, so a sort here orders every
    // page — not just the one on screen.
    sortable: true,
    load: (page, sort) => {
      const features = request.features()
      const ordered = sort ? [...features].sort(byProperty(sort.key, sort.dir)) : features
      return {
        rows: ordered
          .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
          .map((feature) => toRow(feature, request.onZoomTo)),
        total: features.length,
      }
    },
  })
}

/** Re-render if (and only if) this layer is the one currently displayed. */
export function refreshAttributesDrawer(key: string): void {
  if (dataTableKey() === key) refreshDataTable()
}

export function hideAttributesDrawer(): void {
  hideDataTable()
}

/**
 * Numbers compare as numbers, everything else as locale-aware text (so "10"
 * doesn't land before "9", and accented names sort where a French reader
 * expects). Empty values sink to the bottom in both directions — an absent
 * value is not "the smallest one", it just isn't part of the ranking.
 */
function byProperty(key: string, dir: 'asc' | 'desc') {
  const sign = dir === 'asc' ? 1 : -1
  return (a: Feature<Geometry>, b: Feature<Geometry>): number => {
    const av = a.get(key)
    const bv = b.get(key)
    const aEmpty = av === undefined || av === null || av === ''
    const bEmpty = bv === undefined || bv === null || bv === ''
    if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign
    return String(av).localeCompare(String(bv), undefined, { numeric: true }) * sign
  }
}

function columnsOf(request: AttributesRequest, hidden: ReadonlySet<string>): DataTableColumn[] {
  const columns: DataTableColumn[] = []
  for (const feature of request.features().slice(0, COLUMN_SAMPLE)) {
    for (const key of Object.keys(feature.getProperties())) {
      if (key !== 'geometry' && !hidden.has(key) && !columns.some((c) => c.key === key)) {
        // Readable header, raw column name as the tooltip; sorting still
        // goes through `key`.
        columns.push({ key, label: attributeLabel(request.key, request.title, key), title: key })
      }
    }
  }
  return columns
}

function toRow(
  feature: Feature<Geometry>,
  onZoomTo: ((extent: Extent) => void) | undefined,
): DataTableRow {
  const cells = { ...(feature.getProperties() as Record<string, unknown>) }
  delete cells.geometry
  const extent = feature.getGeometry()?.getExtent()
  return {
    cells,
    onShow: onZoomTo && extent ? () => onZoomTo(extent) : undefined,
  }
}
